import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Star, Quote, ArrowRight, Sun, Leaf } from 'lucide-react';
import { ASSETS } from '../components/Layout';

const HomePage = () => {
  const [heroOpacity, setHeroOpacity] = useState(1);
  const heroRef = useRef(null);

  // Scroll fade-out effect for hero
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroHeight = heroRef.current.offsetHeight;
        const scrollY = window.scrollY;
        const newOpacity = Math.max(0, 1 - (scrollY / (heroHeight * 0.7)));
        setHeroOpacity(newOpacity);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Testimonials data
  const testimonials = [
    {
      name: "Janet M.",
      text: "A gem of a place! 5* food and service... All food baked on site and AMAZING!",
      rating: 5
    },
    {
      name: "Marc N.",
      text: "Delicious, plentiful, and amazing service. Enough food to feed double! Simply wonderful.",
      rating: 5
    },
    {
      name: "Helen L.",
      text: "Friendly people, tasteful decor and great outdoor seating. Always fab!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image with Scroll Fade */}
        <div 
          className="absolute inset-0 transition-opacity duration-100"
          style={{ opacity: heroOpacity }}
        >
          <img
            src={ASSETS.hero}
            alt="Bean in Stony interior - Historic apothecary café"
            className="w-full h-full object-cover"
          />
          {/* Darker Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        </div>
        
        {/* Bottom fade to blend into cream background */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#F5F0E1] via-[#F5F0E1]/70 to-transparent pointer-events-none z-[5]"
        />

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="font-accent text-white text-2xl md:text-3xl mb-4 animate-fade-in-up drop-shadow-lg">
            Est. in a 1700s Apothecary
          </p>
          <h1 
            className="font-serif text-5xl md:text-7xl lg:text-8xl text-white font-bold mb-6 animate-fade-in-up animation-delay-100 drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
            data-testid="hero-headline"
          >
            Stony Stratford&apos;s<br />Hidden Gem
          </h1>
          <p className="text-white text-lg md:text-xl max-w-2xl mx-auto mb-10 animate-fade-in-up animation-delay-200 drop-shadow-lg">
            Step into history. Savour the present. Where centuries-old walls whisper 
            stories of a bygone era over artisan coffee and homemade delights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-300">
            <Link
              to="/menu"
              className="bg-white text-walnut px-8 py-3 rounded-full font-medium tracking-wide transition-all duration-300 hover:bg-sage hover:text-white hover:-translate-y-1 shadow-lg inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-menu"
            >
              View Our Menu
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-full font-medium tracking-wide transition-all duration-300 hover:bg-white hover:text-walnut"
              data-testid="hero-cta-contact"
            >
              Make an Enquiry
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"
          style={{ opacity: heroOpacity }}
        >
          <ChevronDown className="w-8 h-8 text-cream/80" />
        </div>
      </section>

      {/* About Preview Section */}
      <section className="section-padding bg-cream" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="relative">
              <img
                src={ASSETS.heritage}
                alt="Historic apothecary drawers at Bean in Stony"
                className="w-full rounded-lg shadow-heritage-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-sage p-6 rounded-lg hidden md:block">
                <p className="font-accent text-walnut text-xl">Since the 1700s</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <p className="text-sage uppercase tracking-widest text-sm font-medium mb-4">Our Heritage</p>
              <h2 className="font-serif text-4xl md:text-5xl text-walnut mb-6">
                A Story Steeped in History
              </h2>
              <div className="prose prose-lg text-walnut/80">
                <p className="first-letter:text-5xl first-letter:font-serif first-letter:text-walnut first-letter:float-left first-letter:mr-3 first-letter:mt-1">
                  Nestled within the charming market town of Stony Stratford lies Bean in Stony — 
                  a café unlike any other. Our building, once a thriving apothecary and chemist 
                  in the 1700s, still carries the echoes of its remarkable past.
                </p>
                <p>
                  The original wooden apothecary drawers, ornate Tiffany-style lamps, and exposed 
                  brick walls create an atmosphere where every corner whispers stories of a bygone era.
                </p>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-8 btn-ghost font-medium"
                data-testid="about-preview-link"
              >
                Discover Our Full Story
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Outdoor Space Teaser */}
      <section className="section-padding bg-sage/10" data-testid="outdoor-teaser-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <p className="text-sage uppercase tracking-widest text-sm font-medium mb-4">Al Fresco Dining</p>
              <h2 className="font-serif text-4xl md:text-5xl text-walnut mb-6">
                Our Secret Garden
              </h2>
              <p className="text-walnut/80 text-lg leading-relaxed mb-6">
                Escape to our hidden oasis — a lush, vine-covered garden terrace draped in 
                cascading greenery. Whether you're enjoying morning coffee or a leisurely 
                afternoon tea, our outdoor space offers a tranquil retreat from the High Street bustle.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-walnut/70">
                  <Sun className="w-5 h-5 text-sage" />
                  <span className="text-sm">Sun-dappled seating</span>
                </div>
                <div className="flex items-center gap-2 text-walnut/70">
                  <Leaf className="w-5 h-5 text-sage" />
                  <span className="text-sm">Vine-covered canopy</span>
                </div>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-8 btn-primary"
                data-testid="outdoor-teaser-link"
              >
                Learn More
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2 relative">
              <img
                src={ASSETS.outdoor}
                alt="Bean in Stony secret garden with vine-covered outdoor seating"
                className="w-full rounded-lg shadow-heritage-lg"
              />
              <div className="absolute -bottom-4 -left-4 bg-walnut p-4 rounded-lg hidden md:block">
                <p className="font-accent text-cream text-lg">Weather permitting!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(75, 54, 33, 0.92), rgba(75, 54, 33, 0.92)), url(${ASSETS.brick})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="testimonials-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sage uppercase tracking-widest text-sm font-medium mb-4">Testimonials</p>
            <h2 className="font-serif text-4xl md:text-5xl text-cream">
              What Our Guests Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="card-testimonial bg-cream/95 relative"
                data-testid={`testimonial-${index}`}
              >
                <Quote className="absolute top-4 right-4 w-8 h-8 text-sage/30" />
                
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-gold text-gold" />
                  ))}
                </div>

                <p className="text-walnut text-lg mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                
                <p className="font-sans font-medium text-walnut-light text-sm uppercase tracking-wider">
                  — {testimonial.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-sage-light/30" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-walnut mb-6">
            Ready to Experience Bean in Stony?
          </h2>
          <p className="text-walnut/80 text-lg mb-10 max-w-2xl mx-auto">
            Whether you're craving our famous afternoon tea, planning an event, 
            or simply seeking a cosy spot for coffee, we'd love to welcome you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/menu"
              className="btn-primary"
              data-testid="cta-menu-btn"
            >
              Explore Our Menu
            </Link>
            <Link
              to="/contact"
              className="btn-secondary"
              data-testid="cta-contact-btn"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
